import {
  Component,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DISABLE_NAV } from './appConstants/DisableNavigationRoutes';
import { IBasicInfo } from './appModels/IBasicInfo';
import { CrashlyticsService } from './appServices/crashlytics.service';
import { AppInitializerService } from './core/appInit/app-initializer.service';
import { BreakpointService } from './core/utilities/breakpoint.service';
import { AutomaticPrintOutboxService } from './core/printing/automatic-print-outbox.service';
import { CashPaymentJournalService } from './core/payments/cash-payment-journal.service';
import { PaymentSaleGuardService } from './core/payments/payment-sale-guard.service';
import { TokenService } from './core/auth/token.service';
import { LogoutResolver } from './appServices/logout/logout.resolver';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  breakpoint$ = this._breakpointService.breakpoint;
  disableNav = false;
  basicInfo: IBasicInfo;
  private routerEventsSubscription: any;
  private sessionInvalidatedSubscription: Subscription;

  constructor(
    private appInitService: AppInitializerService,
    private _breakpointService: BreakpointService,
    private router: Router,
    private crashlytics: CrashlyticsService,
    private automaticPrintOutbox: AutomaticPrintOutboxService,
    private cashPaymentJournal: CashPaymentJournalService,
    private paymentSaleGuard: PaymentSaleGuardService,
    private tokenService: TokenService,
    private injector: Injector,
  ) {
    // Resolve the rich logout graph only after an actual invalidation. Keeping
    // it out of the HTTP interceptor prevents HttpClient -> interceptor ->
    // resolver -> HttpClient dependency cycles at application startup.
    this.sessionInvalidatedSubscription =
      this.tokenService.sessionInvalidated$.subscribe(() => {
        void this.injector.get(LogoutResolver).expireSession();
      });
    this.initCrashlytics();
    void this.appInitService.initializeApp().catch((error) =>
      console.error('Application initialization failed', error),
    );
    this.checkUrlToDisableNav();
  }

  /**
   * Initializes Crashlytics with default settings.
   */
  async initCrashlytics() {
    await this.crashlytics.setEnabled();
    await this.crashlytics.setUserId();
    await this.crashlytics.setCustomKey();
    await this.crashlytics.log();

    const didCrash = await this.crashlytics.didCrashOnPreviousExecution();
    console.log('Did crash last time?', didCrash);
  }


  @HostListener('window:resize', ['$event'])
  onWindowResize(event) {
    this._breakpointService.onResize(event.target.innerWidth);
  }

  ngOnInit(): void {
    this.setActualViewportHeight();
    window.addEventListener('resize', this.setActualViewportHeight);
    void this.automaticPrintOutbox.start().catch((error) =>
      console.error('Automatic print recovery could not start', error),
    );
    void this.resumePaymentRecovery();
  }

  private async resumePaymentRecovery(): Promise<void> {
    try {
      await this.paymentSaleGuard.retireMyPosRail();
    } catch (error) {
      // Fail closed: keep cash/Stripe guards intact if the one-way retirement
      // marker cannot be committed durably.
      console.error(
        'Legacy myPOS payment guard could not be retired; pending cash replay was deferred',
        error,
      );
      return;
    }
    await this.cashPaymentJournal.resumePending().catch((error) =>
      console.error('Cash payment recovery could not start', error),
    );
  }


  setActualViewportHeight = () => {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  private checkUrlToDisableNav() {
    this.routerEventsSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const isDisableNav = DISABLE_NAV.find((e) => event.url.startsWith(e));
        this.disableNav = !!isDisableNav;

        // Only update --vh variable, do not add another event listener
        let vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }
    });
  }

  ngOnDestroy(): void {
    this.sessionInvalidatedSubscription?.unsubscribe();
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
  }
}
